// Global fetch wrapper.
//
// Attaches the logged-in user's JWT (Authorization: Bearer <token>) to every request
// that targets the HRMS backend, and forces re-login on a 401 (expired/invalid session).
// Imported ONCE from src/index.js so all existing fetch() calls are covered without
// editing them individually.
//
// Notes:
//  - Only backend requests get the header (third-party/static requests are untouched).
//  - It never sets Content-Type, so multipart/form-data uploads keep their boundary.
//  - It logs out ONLY on a genuine auth failure: a 401 whose body is flagged
//    `sessionExpired: true` by the backend auth gate. Ordinary "business" 401s
//    (e.g. "you have already checked in") must NOT end the session. 403 (forbidden
//    role) is also left alone; the user stays logged in.

const BACKEND_HOSTS = ["localhost:8000", "hr-backend-72v1.onrender.com"];

function isBackendUrl(input) {
  try {
    const s =
      typeof input === "string"
        ? input
        : input && typeof input.url === "string"
        ? input.url
        : "";
    return BACKEND_HOSTS.some((h) => s.includes(h));
  } catch (e) {
    return false;
  }
}

function getToken() {
  try {
    return localStorage.getItem("userValue") || "";
  } catch (e) {
    return "";
  }
}

if (
  typeof window !== "undefined" &&
  typeof window.fetch === "function" &&
  !window.__ggAuthFetchPatched
) {
  window.__ggAuthFetchPatched = true;
  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input, init) => {
    const options = init ? { ...init } : {};
    const backend = isBackendUrl(input);
    const token = getToken();

    if (backend && token) {
      const headers = new Headers(
        (options && options.headers) ||
          (input && typeof input === "object" && input.headers) ||
          {}
      );
      if (!headers.has("Authorization")) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      options.headers = headers;
    }

    const response = await originalFetch(input, options);

    // Match the /login endpoint EXACTLY — not a substring, so /loginemployee (an
    // authenticated call that CAN legitimately return a sessionExpired 401) is not
    // wrongly exempted from the logout check.
    const isLoginCall = /\/login(\?|$)/.test(
      typeof input === "string" ? input : (input && input.url) || ""
    );
    if (backend && !isLoginCall && response.status === 401 && token) {
      // Distinguish a real auth failure from a business 401 by inspecting the body.
      // Clone so the original response is still fully readable by the caller. Only
      // `sessionExpired: true` (set by the backend auth gate) ends the session.
      response
        .clone()
        .json()
        .then((body) => {
          if (body && body.sessionExpired) {
            try {
              localStorage.removeItem("userValue");
            } catch (e) {
              /* ignore */
            }
            if (window.location.pathname !== "/") {
              window.location.assign("/");
            }
          }
        })
        .catch(() => {
          /* non-JSON / unreadable 401 body -> treat as business error, stay logged in */
        });
    }

    return response;
  };
}
